import { readonly, ref } from 'vue'

import type { MarketEvent } from '@/types/market'

export type SocketStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected'
type EventHandler<T = unknown> = (event: MarketEvent<T>) => void

function websocketUrl(): string {
  const configured = import.meta.env.VITE_WS_PATH ?? '/ws/market'
  if (/^wss?:\/\//.test(configured)) return configured
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${window.location.host}${configured.startsWith('/') ? configured : `/${configured}`}`
}

class MarketSocketClient {
  private socket: WebSocket | null = null
  private reconnectTimer: number | null = null
  private reconnectAttempts = 0
  private intentionalClose = false
  private readonly handlers = new Map<string, Set<EventHandler>>()

  private readonly mutableStatus = ref<SocketStatus>('idle')
  readonly status = readonly(this.mutableStatus)

  connect() {
    if (this.socket?.readyState === WebSocket.OPEN || this.socket?.readyState === WebSocket.CONNECTING) return
    this.intentionalClose = false
    this.mutableStatus.value = this.reconnectAttempts ? 'reconnecting' : 'connecting'

    try {
      this.socket = new WebSocket(websocketUrl())
      this.socket.onopen = () => {
        this.mutableStatus.value = 'connected'
        this.reconnectAttempts = 0
        for (const topic of this.handlers.keys()) this.sendSubscription(topic)
      }
      this.socket.onmessage = (message) => this.handleMessage(message.data)
      this.socket.onerror = () => this.socket?.close()
      this.socket.onclose = () => {
        this.socket = null
        this.mutableStatus.value = 'disconnected'
        if (!this.intentionalClose) this.scheduleReconnect()
      }
    } catch {
      this.mutableStatus.value = 'disconnected'
      this.scheduleReconnect()
    }
  }

  disconnect() {
    this.intentionalClose = true
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.socket?.close()
    this.socket = null
    this.mutableStatus.value = 'idle'
  }

  subscribe<T>(topic: string, handler: EventHandler<T>): () => void {
    const topicHandlers = this.handlers.get(topic) ?? new Set<EventHandler>()
    topicHandlers.add(handler as EventHandler)
    this.handlers.set(topic, topicHandlers)
    if (this.mutableStatus.value === 'connected') this.sendSubscription(topic)
    return () => {
      topicHandlers.delete(handler as EventHandler)
      if (!topicHandlers.size) {
        this.handlers.delete(topic)
        if (this.mutableStatus.value === 'connected') {
          this.socket?.send(JSON.stringify({ action: 'UNSUBSCRIBE', topic }))
        }
      }
    }
  }

  private sendSubscription(topic: string) {
    this.socket?.send(JSON.stringify({ action: 'SUBSCRIBE', topic }))
  }

  private handleMessage(raw: string | ArrayBuffer | Blob) {
    if (typeof raw !== 'string') return
    try {
      const event = JSON.parse(raw) as MarketEvent
      const topic = event.topic ?? '*'
      for (const handler of this.handlers.get(topic) ?? []) handler(event)
      for (const handler of this.handlers.get('*') ?? []) handler(event)
    } catch {
      // Ignore malformed presentation events; REST remains the snapshot source.
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer !== null || this.intentionalClose || !this.handlers.size) return
    const delay = Math.min(30_000, 1_000 * 2 ** this.reconnectAttempts)
    this.reconnectAttempts += 1
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }
}

export const marketSocket = new MarketSocketClient()
